<template>
    <group-backend-layout
        :page-title="'Student Profile - ' + student.full_name"
    >
        <template #page_header>
            <BackNavButton link-text="Go Back"/>

            <div class="mb-8 flex justify-between border-b pb-4">
                <div class="flex flex-col gap-4">
                    <h1 class="text-[28px] font-semibold">
                        {{ student.full_name }}
                    </h1>
                    <div v-if="!student.subscription" class="flex flex-row gap-4">
                        <div class="flex items-center gap-4">
                            <div class="text-yellow-500">
                                No Membership
                            </div>
                            <group-student-extend-membership-modal
                                :student="student"
                            />
                        </div>
                    </div>
                    <div
                        v-else-if="student.subscription && student.subscription.is_pending"
                        class="flex flex-row gap-4"
                    >
                        <div class="flex items-center gap-4">
                            <div class="text-yellow-500">
                                Membership Pending
                            </div>
                            <Link
                                :href="
                                    route('group.student.resend-invite', {
                                        user: student.id,
                                    })
                                "
                                class="py-.5 rounded border border-yellow-500 px-2 text-[14px] text-yellow-500 hover:cursor-pointer hover:bg-yellow-500 hover:text-white"
                            >
                                Resend Invite
                            </Link>
                        </div>
                    </div>
                    <div v-else class="flex flex-row items-center gap-4">
                        <div v-if="student.subscription.is_active" class="">
                            {{ student.subscription.days_left }} days remaining
                        </div>
                        <div
                            v-else
                            class="flex items-center gap-2 text-red-500"
                        >
                            <svg
                                width="18"
                                height="16"
                                viewBox="0 0 18 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M6.83391 1.50195C7.79641 -0.164714 10.2031 -0.164714 11.1647 1.50195L17.2939 12.1253C18.2556 13.792 17.0522 15.8753 15.1281 15.8753H2.87058C0.946409 15.8753 -0.256091 13.792 0.705575 12.1253L6.83308 1.50195H6.83391ZM8.99974 5.87445C9.1655 5.87445 9.32447 5.9403 9.44168 6.05751C9.55889 6.17472 9.62474 6.33369 9.62474 6.49945V9.62445C9.62474 9.79021 9.55889 9.94918 9.44168 10.0664C9.32447 10.1836 9.1655 10.2495 8.99974 10.2495C8.83398 10.2495 8.67501 10.1836 8.5578 10.0664C8.44059 9.94918 8.37474 9.79021 8.37474 9.62445V6.49945C8.37474 6.33369 8.44059 6.17472 8.5578 6.05751C8.67501 5.9403 8.83398 5.87445 8.99974 5.87445ZM8.99974 12.7495C9.1655 12.7495 9.32447 12.6836 9.44168 12.5664C9.55889 12.4492 9.62474 12.2902 9.62474 12.1245C9.62474 11.9587 9.55889 11.7997 9.44168 11.6825C9.32447 11.5653 9.1655 11.4995 8.99974 11.4995C8.83398 11.4995 8.67501 11.5653 8.5578 11.6825C8.44059 11.7997 8.37474 11.9587 8.37474 12.1245C8.37474 12.2902 8.44059 12.4492 8.5578 12.5664C8.67501 12.6836 8.83398 12.7495 8.99974 12.7495Z"
                                    fill="#CA3A31"
                                />
                            </svg>
                            <div class="">Membership Expired</div>
                        </div>
                        <div class="" v-if="subscriptionIsGroupManaged">
                            <div
                                class="border-l border-gray-450 pl-4"
                                v-if="$page.props.groupHasMembershipsAvailable"
                            >
                                <group-student-extend-membership-modal
                                    :student="student"
                                />
                            </div>
                            <div class="border-l border-gray-450 pl-4" v-else>
                                <GroupBuyMembershipModal>
                                    <div class="btn btn-primary">
                                        <div class="flex items-center gap-2">
                                            <svg
                                                width="13"
                                                height="12"
                                                viewBox="0 0 13 12"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M6.5 1.04167V10.9583"
                                                    stroke="white"
                                                    stroke-width="1.41667"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                                <path
                                                    d="M1.54175 6H11.4584"
                                                    stroke="white"
                                                    stroke-width="1.41667"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                            <span
                                            ><span class="hidden md:inline"
                                            >Buy</span
                                            >
                                                Memberships</span
                                            >
                                        </div>
                                    </div>
                                </GroupBuyMembershipModal>
                            </div>
                        </div>
                        <div v-else class="border-l border-gray-450 pl-4 flex items-center gap-4">
                            <Tooltip width-px="300">
                                <template #trigger>
                                    <div class="text-gray-600">
                                        Self-managed Subscription
                                    </div>
                                </template>
                                <template #content>
                                    This student has a self-managed
                                    subscription. You will not be able to extend
                                    them with a group membership until their
                                    subscription has expired.
                                </template>
                            </Tooltip>
                            <group-convert-self-managed-student-to-group-managed-modal :student="student"/>
                        </div>
                    </div>
                </div>
                <div
                    class="hidden items-center gap-4 lg:flex"
                    v-if="form.isDirty"
                >
                    <div class="btn btn-outline" @click="reset">
                        Discard Changes
                    </div>
                    <div class="btn btn-primary" @click="save">
                        Save Changes
                    </div>
                </div>
            </div>
        </template>

        <div class="flex justify-between gap-8">
            <div v-if="!subscriptionIsGroupManaged" class="flex gap-4">
                <div class="">
                    <div
                        class="group relative flex h-[160px] w-[160px] flex-col items-center justify-center rounded border border-gray-400 bg-gray-250 bg-cover bg-center bg-no-repeat p-4 text-center"
                        :style="'background-image: url(' + student.avatar + ')'"
                    >
                        <div
                            class="font-semibold text-gray-600"
                            v-if="!student.avatar"
                        >
                            No Avatar Set
                        </div>
                    </div>
                </div>
                <div class="flex flex-col gap-4">
                    <div class="flex flex-row gap-2">
                        <form-input
                            label="First name"
                            name="first_name"
                            v-model="form.firstName"
                            :disabled="!subscriptionIsGroupManaged"
                        />
                        <form-input
                            label="Last name"
                            name="last_name"
                            v-model="form.lastName"
                            :disabled="!subscriptionIsGroupManaged"
                        />
                    </div>
                    <div class="flex flex-row gap-2">
                        <form-input
                            label="Email"
                            name="email"
                            type="email"
                            v-model="form.email"
                            :disabled="!subscriptionIsGroupManaged"
                        />
                    </div>
                    <div class="relative flex flex-row gap-2">
                        <form-input
                            label="Password"
                            name="password"
                            type="password"
                            model-value="dummy-password"
                            :disabled="true"
                        />
                        <Link
                            :href="
                                route('user.send-password-reset', {
                                    user_id: student.id,
                                })
                            "
                            v-if="subscriptionIsGroupManaged"
                            class="link absolute right-0 top-0 text-[14px]"
                        >Send password reset link
                        </Link>
                    </div>
                    <div class="flex flex-row gap-2">
                        <div class="relative w-full">
                            <label class="mb-2 block text-[14px] font-semibold">
                                <span>Classroom</span>
                            </label>
                            <div class="relative flex w-full items-center">
                                <GroupStudentClassroomSelect
                                    :classroom-options="classroomOptions"
                                    :instructor-options="instructorOptions"
                                    v-model="form.classroomId"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-row gap-2">
                        <form-select
                            label="Provider Level"
                            name="provider_level_id"
                            v-model="form.providerLevelId"
                            :options="providerLevels"
                            :disabled="!subscriptionIsGroupManaged"
                        />
                    </div>
                </div>
            </div>
            <div v-else class="flex gap-4">
                <div class="">
                    <div
                        v-if="student.avatar"
                        class="group relative h-[160px] w-[160px] rounded border border-gray-400 bg-gray-250 bg-cover bg-center bg-no-repeat"
                        :style="'background-image: url(' + student.avatar + ')'"
                    >
                        <div
                            class="invisible absolute bottom-0 flex w-full group-hover:visible"
                        >
                            <span
                                class="flex-grow rounded-b bg-black/75 px-2 py-1 text-center text-[14px] font-medium text-white hover:cursor-pointer hover:bg-white hover:text-black"
                                @click="editAvatar()"
                                title="Edit / Remove"
                            >
                                Edit / Remove
                            </span>
                        </div>
                    </div>

                    <drag-image-upload-field
                        v-else
                        v-model="form.avatar"
                        image-preview-css="h-[160px] w-[160px] border border-gray-400 bg-gray-250"
                    >
                        <div class="text-center">
                            <div class="">Upload a student&nbsp;avatar</div>
                            <div class="text-[12px] font-medium">
                                (jpg or png)
                            </div>
                        </div>
                    </drag-image-upload-field>
                </div>
                <div class="flex flex-col gap-4">
                    <div class="flex flex-row gap-2">
                        <form-input
                            label="First name"
                            name="first_name"
                            v-model="form.firstName"
                            :disabled="!subscriptionIsGroupManaged"
                        />
                        <form-input
                            label="Last name"
                            name="last_name"
                            v-model="form.lastName"
                            :disabled="!subscriptionIsGroupManaged"
                        />
                    </div>
                    <div class="flex flex-row gap-2">
                        <form-input
                            label="Email"
                            name="email"
                            type="email"
                            v-model="form.email"
                            :disabled="!subscriptionIsGroupManaged"
                        />
                    </div>
                    <div class="relative flex flex-row gap-2">
                        <form-input
                            label="Password"
                            name="password"
                            type="password"
                            model-value="dummy-password"
                            :disabled="true"
                        />

                        <Link
                            :href="
                                route('user.send-password-reset', {
                                    user_id: student.id,
                                })
                            "
                            v-if="subscriptionIsGroupManaged"
                            class="link absolute right-0 top-0 text-[14px]"
                        >Send password reset link
                        </Link>
                    </div>
                    <div class="flex flex-row gap-2">
                        <div class="relative w-full">
                            <label class="mb-2 block text-[14px] font-semibold">
                                <span>Classroom</span>
                            </label>
                            <div class="relative flex w-full items-center">
                                <GroupStudentClassroomSelect
                                    :classroom-options="classroomOptions"
                                    :instructor-options="instructorOptions"
                                    v-model="form.classroomId"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-row gap-2">
                        <form-select
                            label="Provider Level"
                            name="provider_level_id"
                            v-model="form.providerLevelId"
                            :options="providerLevels"
                            :disabled="!subscriptionIsGroupManaged"
                        />
                    </div>
                </div>
            </div>

            <div class="">
                <group-student-performance-summary :student="student"/>
            </div>
        </div>

        <div class="mt-12" v-if="student.performance">
            <div class="text-[20px] font-semibold">Performance</div>
            <group-student-performance-overview-table
                :student-performance="student.performance"
            />
        </div>

        <div class="mt-12" v-if="!subscriptionIsGroupManaged">
            <div class="text-[20px] font-semibold">
                Self-managed Subscription
            </div>
            <div class="">
                Student has a self-managed subscription that
                <span v-if="student.subscription.renews_at">renews</span
                ><span v-else>expires</span> on
                <span class="font-semibold">{{
                        dayjs(student.subscription.ends_at).format(
                            $page.props.date_formats.short_nice
                        )
                    }}</span>
            </div>
        </div>

        <div class="mt-12" v-if="membershipHistory.length">
            <div class="text-[20px] font-semibold">Membership History</div>
            <GroupStudentMembershipHistoryTable
                :memberships="membershipHistory"
            />
        </div>
    </group-backend-layout>
</template>

<script>
import Tooltip from "@/Components/Tooltip";
import GroupBackendLayout from "@/_Layouts/GroupBackendLayout";
import FormInput from "@/Components/FormInput";
import DragImageUploadField from "@/Components/DragImageUploadField";
import FormSelect from "@/Components/FormSelect";
import GroupStudentPerformanceSummary from "@/Components/GroupStudentPerformanceSummary";
import GroupStudentPerformanceOverviewTable from "@/Components/GroupStudentPerformanceOverviewTable";
import GroupStudentMembershipHistoryTable from "@/Components/GroupStudentMembershipHistoryTable";
import GroupStudentExtendMembershipModal from "@/Components/GroupStudentExtendMembershipModal";
import GroupStudentClassroomSelect from "@/Pages/GroupBackend/GroupStudentClassroomSelect.vue";
import BackNavButton from "@/Pages/GroupBackend/BackNavButton.vue";
import GroupBuyMembershipModal from "@/Components/GroupBuyMembershipModal";
import GroupConvertSelfManagedStudentToGroupManagedModal
    from "@/Pages/GroupBackend/GroupConvertSelfManagedStudentToGroupManagedModal.vue";

export default {
    name: "GroupStudentShow",
    components: {
        GroupConvertSelfManagedStudentToGroupManagedModal,
        Tooltip,
        BackNavButton,
        GroupStudentClassroomSelect,
        GroupStudentExtendMembershipModal,
        GroupStudentMembershipHistoryTable,
        GroupStudentPerformanceOverviewTable,
        GroupStudentPerformanceSummary,
        FormSelect,
        GroupBackendLayout,
        FormInput,
        DragImageUploadField,
        GroupBuyMembershipModal,
    },
    props: {
        student: {
            type: Object,
            default: {},
        },
        classroomOptions: {
            type: Array,
            default: [],
        },
        instructorOptions: {
            type: Array,
            default: [],
        },
        providerLevels: Array,
        membershipHistory: {
            type: Array,
            default: [],
        },
    },
    data() {
        return {
            form: this.$inertia.form({
                avatar: this.student.avatar,
                firstName: this.student.first_name,
                lastName: this.student.last_name,
                email: this.student.email,
                classroomId: this.student.classroom?.id,
                providerLevelId: this.student.provider_level_id,
            }),
        };
    },
    methods: {
        editAvatar() {
            this.student.avatar = null;
            this.form.avatar = null;
        },
        save() {
            let vm = this;

            this.$inertia.post(
                route(route().current(), {user: this.student.id}),
                this.form,
                {
                    only: ["student"],
                    onSuccess: (response) => {
                        (vm.form = vm.$inertia.form({
                            avatar: vm.$page.props.student.avatar,
                            firstName: vm.$page.props.student.first_name,
                            lastName: vm.$page.props.student.last_name,
                            email: vm.$page.props.student.email,
                            classroomId: vm.$page.props.student.classroom?.id,
                            providerLevelId:
                            vm.$page.props.student.provider_level_id,
                        })),
                            (this.$page.props.flash.success = ["User updated"]);
                    },
                    onError: () => {
                        this.$page.props.flash.error = [
                            "Problem updating user. Please try again",
                        ];
                    },
                }
            );
        },
        reset() {
            this.$inertia.visit(
                route(route().current(), {user: this.student.id})
            );
        },
    },
    computed: {
        subscriptionIsGroupManaged() {

            if (!this.student.subscription) {
                return true;
            }

            return this.student.subscription.plan.type === 1;
        },
    },
};
</script>
